<template>
  <div class="section-stack">
    <!-- Hero Section -->
    <section class="relative bg-gray-900 text-white overflow-hidden hero-section">
      <div
        class="absolute inset-0 hero-image"
        :style="{ backgroundImage: `url(${heroImage})` }"
        aria-hidden="true"
      ></div>
      <div class="absolute inset-0 bg-gradient-to-r from-black to-transparent opacity-70"></div>
      <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
        <div class="max-w-3xl" :class="{ 'ml-auto': $i18n.locale === 'ar' }">
          <p class="font-semibold mb-3">
            <span class="text-[#eb0017]">{{ $t('landing.hero.kickerBrand') }}</span> | {{ $t('landing.hero.kickerSuffix') }}
          </p>
          <h1 ref="heroTitle" class="text-4xl md:text-6xl font-bold mb-4">
            {{ $t('landing.hero.title') }}
          </h1>
          <p ref="heroSubtitle" class="text-xl md:text-2xl mb-8 text-gray-200">
            {{ $t('landing.hero.subtitle') }}
          </p>
          <div ref="heroCta" class="flex flex-col sm:flex-row gap-4">
            <AppButton to="/next-step">
              {{ $t('landing.hero.primaryCta') }}
            </AppButton>
            <AppButton to="/approach" variant="secondary">
              {{ $t('landing.hero.secondaryCta') }}
            </AppButton>
          </div>
          <p class="mt-6 text-sm text-gray-300 max-w-2xl">
            {{ $t('landing.hero.roleNote') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Domains Section -->
    <section class="section bg-gray-100 dark:bg-gray-800">
      <div class="container mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold mb-4">{{ $t('landing.domains.title') }}</h2>
          <p class="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {{ $t('landing.domains.subtitle') }}
          </p>
          <div class="w-16 h-1 bg-primary mx-auto"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="card hover:shadow-lg transition-all" ref="serviceCard1">
            <div class="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-shield-alt text-xl text-white"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">{{ $t('landing.domains.items.prevention.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.domains.items.prevention.description') }}
            </p>
          </div>

          <div class="card hover:shadow-lg transition-all" ref="serviceCard2">
            <div class="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-user-clock text-xl text-white"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">{{ $t('landing.domains.items.absence.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.domains.items.absence.description') }}
            </p>
          </div>

          <div class="card hover:shadow-lg transition-all" ref="serviceCard3">
            <div class="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-people-carry text-xl text-white"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">{{ $t('landing.domains.items.employability.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.domains.items.employability.description') }}
            </p>
          </div>

          <div class="card hover:shadow-lg transition-all" ref="serviceCard4">
            <div class="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
              <i class="fas fa-chart-line text-xl text-white"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">{{ $t('landing.domains.items.analytics.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.domains.items.analytics.description') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      class="section-divider"
      :style="{ backgroundImage: `url(${dividerImages.contextToRole})` }"
      aria-hidden="true"
    >
      <div class="section-divider__overlay"></div>
    </section>

    <!-- Strategic context -->
    <section class="section">
      <div class="container mx-auto">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold mb-4">{{ $t('landing.context.title') }}</h2>
          <p class="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {{ $t('landing.context.subtitle') }}
          </p>
          <div class="w-16 h-1 bg-primary mx-auto"></div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div class="card">
            <h3 class="text-xl font-bold mb-3">{{ $t('landing.context.items.strategic.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.context.items.strategic.description') }}
            </p>
          </div>
          <div class="card">
            <h3 class="text-xl font-bold mb-3">{{ $t('landing.context.items.performance.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.context.items.performance.description') }}
            </p>
          </div>
          <div class="card">
            <h3 class="text-xl font-bold mb-3">{{ $t('landing.context.items.shift.title') }}</h3>
            <p class="text-gray-600 dark:text-gray-300">
              {{ $t('landing.context.items.shift.description') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      class="section-divider"
      :style="{ backgroundImage: `url(${dividerImages.roleToCta})` }"
      aria-hidden="true"
    >
      <div class="section-divider__overlay"></div>
    </section>

    <!-- Who it is for / why it matters -->
    <section class="section">
      <div class="container mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div ref="aboutContent">
            <h2 class="text-3xl font-bold mb-4">{{ $t('landing.aonRole.title') }}</h2>
            <div class="w-16 h-1 bg-primary mb-6"></div>
            <p class="text-gray-600 dark:text-gray-300 mb-4">
              {{ $t('landing.aonRole.paragraph1') }}
            </p>
            <p class="text-gray-600 dark:text-gray-300 mb-6">
              {{ $t('landing.aonRole.paragraph2') }}
            </p>
          </div>
            <div class="card border border-gray-200 dark:border-gray-700 mb-6">
              <h3 class="text-xl font-bold mb-3">{{ $t('landing.aonRole.differenceTitle') }}</h3>
              <div class="space-y-3 text-gray-600 dark:text-gray-300">
                <p><span class="font-semibold">{{ $t('landing.aonRole.difference.insurance.title') }}</span> {{ $t('landing.aonRole.difference.insurance.description') }}</p>
                <p><span class="font-semibold">{{ $t('landing.aonRole.difference.advisory.title') }}</span> {{ $t('landing.aonRole.difference.advisory.description') }}</p>
                <p>{{ $t('landing.aonRole.difference.complement') }}</p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex items-start">
                <i class="fas fa-check-circle text-primary mt-1 mr-2"></i>
                <span>{{ $t('landing.aonRole.bullets.diagnosis') }}</span>
              </div>
              <div class="flex items-start">
                <i class="fas fa-check-circle text-primary mt-1 mr-2"></i>
                <span>{{ $t('landing.aonRole.bullets.interventions') }}</span>
              </div>
              <div class="flex items-start">
                <i class="fas fa-check-circle text-primary mt-1 mr-2"></i>
                <span>{{ $t('landing.aonRole.bullets.implementation') }}</span>
              </div>
              <div class="flex items-start">
                <i class="fas fa-check-circle text-primary mt-1 mr-2"></i>
                <span>{{ $t('landing.aonRole.bullets.monitoring') }}</span>
              </div>
            </div>
          <div ref="aboutImage" class="card">
            <h3 class="text-xl font-bold mb-3">{{ $t('landing.questions.title') }}</h3>
            <div class="space-y-3 text-gray-600 dark:text-gray-300">
              <p>{{ $t('landing.questions.items.risk') }}</p>
              <p>{{ $t('landing.questions.items.drivers') }}</p>
              <p>{{ $t('landing.questions.items.plan') }}</p>
              <p>{{ $t('landing.questions.items.measure') }}</p>
            </div>
            <div class="mt-6">
              <router-link to="/evidence" class="text-primary hover:underline inline-flex items-center">
                {{ $t('landing.questions.cta') }}
                <i class="fas fa-arrow-right ml-2"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="section-divider"
      :style="{ backgroundImage: `url(${dividerImages.beforeFinalCta})` }"
      aria-hidden="true"
    >
      <div class="section-divider__overlay"></div>
    </section>

    <!-- Call to Action -->
    <section class="bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white py-16">
      <div class="container mx-auto px-4 text-center">
        <h2 class="text-3xl font-bold mb-6">{{ $t('landing.cta.title') }}</h2>
        <p class="text-gray-600 dark:text-white/90 text-xl mb-8 max-w-3xl mx-auto">
          {{ $t('landing.cta.subtitle') }}
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-4">
          <AppButton to="/next-step">
            {{ $t('landing.cta.primaryCta') }}
          </AppButton>
          <AppButton to="/approach" variant="secondary">
            {{ $t('landing.cta.secondaryCta') }}
          </AppButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import gsap from 'gsap';
import AppButton from '@/components/ui/AppButton.vue';

const heroImage = '/images/landing/hero.jpg';

const dividerImages = {
  domainsToContext: '/images/landing/divider-1.jpg',
  contextToRole: '/images/landing/divider-2.jpg',
  roleToCta: '/images/landing/divider-3.jpg',
  beforeFinalCta: '/images/landing/divider-4.jpg',
} as const;

// Refs for animation elements
const heroTitle = ref(null);
const heroSubtitle = ref(null);
const heroCta = ref(null);
const serviceCard1 = ref(null);
const serviceCard2 = ref(null);
const serviceCard3 = ref(null);
const serviceCard4 = ref(null);
const aboutImage = ref(null);
const aboutContent = ref(null);

onMounted(() => {
  // Hero section animations
  const tl = gsap.timeline();
  tl.from(heroTitle.value, { opacity: 0, y: 30, duration: 0.8 })
    .from(heroSubtitle.value, { opacity: 0, y: 20, duration: 0.8 }, "-=0.4")
    .from(heroCta.value, { opacity: 0, y: 20, duration: 0.8 }, "-=0.4");

  // Service cards animation with stagger
  gsap.from([serviceCard1.value, serviceCard2.value, serviceCard3.value, serviceCard4.value], {
    y: 30,
    stagger: 0.2,
    duration: 0.6,
    scrollTrigger: {
      trigger: serviceCard1.value,
      start: "top 80%"
    }
  });

  // About section animations
  gsap.from(aboutImage.value, {
    opacity: 0,
    x: -50,
    duration: 0.8,
    scrollTrigger: {
      trigger: aboutImage.value,
      start: "top 75%"
    }
  });
  
  gsap.from(aboutContent.value, {
    opacity: 0,
    x: 50,
    duration: 0.8,
    scrollTrigger: {
      trigger: aboutContent.value,
      start: "top 75%"
    }
  });
});
</script>

<style scoped>
.hero-section {
  height: 80vh;
  min-height: 600px;
}

.hero-image {
  background-size: cover;
  background-position: center;
  transform: scale(1.02);
}

.section-divider {
  position: relative;
  height: 220px;
  width: 100%;
  background-size: cover;
  background-position: center;
}

@media (min-width: 768px) {
  .section-divider {
    height: 600px;
  }
}

.section-divider__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.25));
}
</style>
